import { SUPPORTED_CODES } from "./supportedCodes";

/**
 * Geo-based language detector for i18next
 * ────────────────────────────────────────
 * Registered in the i18next detection chain at position 2:
 *     order: ["localStorage", "geo", "navigator", "htmlTag"]
 *
 * How it works:
 *  1. The user's explicit choice (localStorage `dc_lang`) is checked FIRST
 *     by i18next itself — this detector never runs if that's present.
 *     User agency always wins.
 *  2. If no saved preference, this detector fires and calls our own
 *     `/api/geo` endpoint. That endpoint reads Cloudflare's `CF-IPCountry`
 *     header (added at the CF edge on every request to dannyclans.solutions)
 *     and returns `{ country, lang }` where `lang` is a suggested UI locale.
 *  3. i18next then continues to `navigator.language` and finally `htmlTag`
 *     if geo returned null.
 *
 * SEO-safe by design:
 *  - Never redirects URLs. `/pricing` stays `/pricing` regardless of
 *    the visitor's country; hreflang tags handle search-engine variants.
 *  - The prerendered HTML (react-snap output) is always in English —
 *    so bots see one canonical language snapshot per URL. Only real
 *    browser users see localised UI after JS hydration.
 *  - The `/api/geo` call is async and fire-and-forget: if it fails,
 *    times out, or the backend is unreachable, i18next falls back to
 *    the next step in the chain (navigator language) with no error.
 *
 * Zero PII: the endpoint only echoes the country code, never the IP.
 */

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

// Small sync cache. i18next's LanguageDetector API is synchronous; we
// fire the /api/geo request early (on module load) and stash the result
// so `lookup()` can return it later. Falls back to `null` (→ next
// detector in chain) if the request isn't complete when i18next asks.
let cachedGeoLang = null;

if (typeof window !== "undefined" && typeof fetch === "function") {
  // Skip during the react-snap prerender pass — react-snap sets a
  // literal "ReactSnap" user-agent (see package.json > reactSnap).
  // We don't want the prerendered HTML to bake in a random locale.
  const isPrerender = /ReactSnap/i.test(navigator.userAgent || "");
  if (!isPrerender) {
    // 3s hard timeout so a hung backend never delays initial paint.
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 3000);
    fetch(`${BACKEND}/api/geo`, {
      method: "GET",
      credentials: "omit",
      signal: controller.signal,
      headers: { Accept: "application/json" },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        clearTimeout(timeoutId);
        if (!data || !data.lang) return;
        if (!SUPPORTED_CODES.has(data.lang)) return;
        cachedGeoLang = data.lang;
        // If i18next has already resolved to fallback English before this
        // callback landed, re-apply the suggested language now — but ONLY
        // if the user still hasn't explicitly picked one in the meantime.
        try {
          const saved = localStorage.getItem("dc_lang");
          if (saved) return; // user already chose; never override
          // Dynamic import avoids a circular import at module top-level.
          import("./index").then((mod) => {
            const i18n = mod.default;
            if (i18n && i18n.resolvedLanguage !== data.lang) {
              i18n.changeLanguage(data.lang);
            }
          });
        } catch { /* localStorage may be unavailable (private mode) */ }
      })
      .catch(() => {
        clearTimeout(timeoutId);
        // Fail silent — i18next continues its detection chain.
      });
  }
}

/**
 * i18next-browser-languagedetector custom-detector shape.
 * Only `lookup` is needed for one-way detection; we don't cache
 * (localStorage is already the cache layer in the chain).
 */
export const geoDetector = {
  name: "geo",
  lookup: () => cachedGeoLang || undefined,
  cacheUserLanguage: () => { /* no-op — localStorage is the cache */ },
};
